// Add new table column definitions to this file as new custom grids are
// required. Only this file should change within the shared directory, leave
// everything else as is unless we have to also support additional functionality

import type { TableHeadCellProps } from 'src/components/table';

import { InvoiceColumns, SUPPORTED_TABLES_ENUM, SupportedBopTable } from 'src/bop-view-definitions';

import { HEADER_ACTION_WIDTH } from '../constants';

export type CustomizableTableColumnDefinition = Omit<TableHeadCellProps, 'id' | 'label'> & {
  labelKey?: string;
};

export const INVOICE_ACTION_COLUMN_ID = 'actions';

export const INVOICE_TABLE_COLUMNS = {
  [InvoiceColumns.CUSTOMER_ID]: {
    labelKey: 'tables.columns.customer',
    width: '22%',
    dataTestId: 'th-customer',
    sortType: 'string',
  },
  [InvoiceColumns.INVOICE_NUMBER]: {
    labelKey: 'tables.columns.invoiceNo',
    width: '15%',
    dataTestId: 'th-invoice-no',
    sortType: 'string',
  },
  [InvoiceColumns.COUNTRY]: {
    labelKey: 'tables.columns.country',
    width: '10%',
    dataTestId: 'th-country',
  },
  [InvoiceColumns.DUE_DATE]: {
    labelKey: 'tables.columns.dueDate',
    width: '5%',
    dataTestId: 'th-due-date',
  },
  [InvoiceColumns.INVOICE_DATE]: {
    labelKey: 'tables.columns.invoiceDate',
    width: '10%',
    dataTestId: 'th-invoice-date',
  },
  [InvoiceColumns.TOTAL_REMAINING_BALANCE]: {
    labelKey: 'tables.columns.trb',
    align: 'right',
    width: '10%',
    dataTestId: 'th-total-remaining-balance',
  },
  [InvoiceColumns.STAGE]: {
    labelKey: 'tables.columns.stage',
    align: 'center',
    width: '0%',
    dataTestId: 'th-stage',
  },
  [InvoiceColumns.STATUS]: {
    labelKey: 'tables.columns.status',
    align: 'center',
    width: '0%',
    dataTestId: 'th-status',
  },
  [InvoiceColumns.PAYMENT_STATUS]: {
    labelKey: 'tables.columns.paymentStatus',
    align: 'center',
    width: '0%',
    dataTestId: 'th-payment-status',
  },
  // New Fields
  [InvoiceColumns.SERVICE_LINE_NAME]: {
    labelKey: 'tables.columns.serviceLineName',
    align: 'center',
    width: '0%',
    dataTestId: 'th-service-line-name',
  },
  [InvoiceColumns.FEE]: {
    labelKey: 'tables.columns.fee',
    align: 'center',
    width: '0%',
    dataTestId: 'th-fee',
  },
  [InvoiceColumns.ORIGINAL_TOTAL_AMOUNT]: {
    labelKey: 'tables.columns.originalTotalAmount',
    align: 'center',
    width: '0%',
    dataTestId: 'th-original-total-amount',
  },
  [InvoiceColumns.PAYMENT_METHOD]: {
    labelKey: 'tables.columns.paymentMethod',
    align: 'center',
    width: '0%',
    dataTestId: 'th-payment-method',
  },
  [InvoiceColumns.PAYMENT_DATE]: {
    labelKey: 'tables.columns.paymentDate',
    align: 'center',
    width: '0%',
    dataTestId: 'th-payment-date',
  },
  [InvoiceColumns.lAST_ESCALATION_DATE]: {
    labelKey: 'tables.columns.lastEscalationDate',
    align: 'center',
    width: '0%',
    dataTestId: 'th-last-escalation-date',
  },
  [InvoiceColumns.WHEN_ESCALATING_TO_DEBT_COLLECTION]: {
    labelKey: 'tables.columns.whenEscalatingToDebtCollection',
    align: 'center',
    width: '0%',
    dataTestId: 'th-when-escalating-to-debt-collection',
  },
  [InvoiceColumns.PAID_AMOUNT]: {
    labelKey: 'tables.columns.paidAmount',
    align: 'center',
    width: '0%',
    dataTestId: 'th-paid-amount',
  },
  [InvoiceColumns.DAYS_OVERDUE]: {
    labelKey: 'tables.columns.daysOverdue',
    align: 'center',
    width: '0%',
    dataTestId: 'th-days-overdue',
  },
  [InvoiceColumns.ORG_NO]: {
    labelKey: 'tables.columns.orgNo',
    align: 'center',
    width: '0%',
    dataTestId: 'th-org-no',
  },
  [InvoiceColumns.DISTRIBUTION_CHANNEL]: {
    labelKey: 'tables.columns.distributionChannel',
    align: 'center',
    width: '0%',
    dataTestId: 'th-distribution-channel',
  },
  [InvoiceColumns.VAT_AMOUNT]: {
    labelKey: 'tables.columns.vatAmount',
    align: 'center',
    width: '0%',
    dataTestId: 'th-vat-amount',
  },
  [InvoiceColumns.PAYMENT_REFERENCE]: {
    labelKey: 'tables.columns.paymentReference',
    align: 'center',
    width: '0%',
    dataTestId: 'th-payment-reference',
  },
  [InvoiceColumns.CURRENCY]: {
    labelKey: 'tables.columns.currency',
    align: 'center',
    width: '0%',
    dataTestId: 'th-currency',
  },
  [InvoiceColumns.CLIENT_NAME]: {
    labelKey: 'tables.columns.clientName',
    align: 'center',
    width: '0%',
    dataTestId: 'th-client-name',
  },
  [InvoiceColumns.REMINDER_DATE]: {
    labelKey: 'tables.columns.reminderDate',
    align: 'center',
    width: '0%',
    dataTestId: 'th-reminder-date',
  },
  [InvoiceColumns.PAUSED_TYPE]: {
    labelKey: 'tables.columns.pausedType',
    align: 'center',
    width: '0%',
    dataTestId: 'th-paused-type',
  },
  [InvoiceColumns.PAUSED_LEVEL]: {
    labelKey: 'tables.columns.pausedLevel',
    align: 'center',
    width: '0%',
    dataTestId: 'th-paused-level',
  },
  [InvoiceColumns.PAUSED_DURATION]: {
    labelKey: 'tables.columns.pausedDuration',
    align: 'center',
    width: '0%',
    dataTestId: 'th-paused-duration',
  },
  [InvoiceColumns.PDF_LINK]: {
    labelKey: 'tables.columns.pdfUrl',
    align: 'center',
    width: '0%',
    dataTestId: 'th-pdf-url',
  },
  /**
   * Frontend-only column.
   * Not part of the shared BOP view definition because it does not represent backend data.
   */
  [INVOICE_ACTION_COLUMN_ID]: {
    width: '0%',
    sortable: false,
    dataTestId: 'th-actions',
    /**
     * The header cell's content is absolutely positioned, so it adds nothing to
     * the cell's in-flow width. With no body cell to widen the column either —
     * the empty and loading rows span every column — the column collapses to
     * zero width and the pinned cell paints its content past the right edge of
     * the scroll area, out of sight.
     */
    sx: { minWidth: HEADER_ACTION_WIDTH },
  },
} satisfies Partial<
  Record<InvoiceColumns | typeof INVOICE_ACTION_COLUMN_ID, CustomizableTableColumnDefinition>
>;

export const CUSTOMIZABLE_TABLE_COLUMNS: Record<
  SupportedBopTable,
  Partial<Record<string, CustomizableTableColumnDefinition>>
> = {
  [SUPPORTED_TABLES_ENUM.INVOICES]: INVOICE_TABLE_COLUMNS,
  // The customer profile invoice list renders the same columns; only the
  // persisted view definition is separate.
  [SUPPORTED_TABLES_ENUM.CUSTOMER_INVOICES]: INVOICE_TABLE_COLUMNS,
};
